import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchInvestOrders, type InvestTradeOrder } from './api';

export const INVEST_LAST_SEEN_MOVE_KEY = 'investLastSeenMove';

function storedLastSeen() {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(INVEST_LAST_SEEN_MOVE_KEY);
}

function timestamp(value: string | null | undefined) {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

export interface InvestMoveNotifications {
  orders: InvestTradeOrder[];
  newOrders: InvestTradeOrder[];
  newCount: number;
  latestAt: string | null;
  isPending: boolean;
  markSeen: () => void;
}

export function useInvestMoveNotifications(enabled = true): InvestMoveNotifications {
  const [lastSeen, setLastSeen] = useState<string | null>(storedLastSeen);
  const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  const query = useQuery({
    queryKey: ['invest-move-notifications'],
    queryFn: () => fetchInvestOrders(0, 20),
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled && visible ? 5 * 60_000 : false,
    refetchIntervalInBackground: false,
    retry: 1,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    const onVisibility = () => {
      const nextVisible = document.visibilityState === 'visible';
      setVisible(nextVisible);
      if (nextVisible) query.refetch();
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === INVEST_LAST_SEEN_MOVE_KEY) setLastSeen(event.newValue);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('storage', onStorage);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('storage', onStorage);
    };
  }, [enabled, query.refetch]);

  const orders = query.data?.content ?? [];
  const latestAt = orders[0]?.executedAt ?? null;
  const newOrders = useMemo(() => {
    const seenAt = timestamp(lastSeen);
    if (seenAt == null) return orders;
    return orders.filter((order) => (timestamp(order.executedAt) ?? 0) > seenAt);
  }, [lastSeen, orders]);
  const markSeen = useCallback(() => {
    if (!latestAt) return;
    window.localStorage.setItem(INVEST_LAST_SEEN_MOVE_KEY, latestAt);
    setLastSeen(latestAt);
  }, [latestAt]);

  return { orders, newOrders, newCount: newOrders.length, latestAt, isPending: query.isPending, markSeen };
}
